import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import PageHero from '../components/PageHero';
import FinalCTA from '../components/FinalCTA';
import Seo, { personSchema } from '../components/Seo';
import { ArrowRight, Mail, Calendar, Quote, MapPin } from 'lucide-react';
import { THERAPISTS } from '../mock';

export default function TherapistDetail() {
  const { slug } = useParams();
  const t = THERAPISTS.find(x => x.slug === slug);
  if (!t) return <Navigate to="/team" replace />;
  const bioParas = Array.isArray(t.bioLong) ? t.bioLong : [t.bioLong];
  return (
    <>
      <Seo
        title={`${t.name}, ${t.credentials}`}
        description={`Meet ${t.name} — ${t.role} at Chesapeake Counseling Center in Chesapeake, VA. ${t.bioShort}`}
        path={`/team/${t.slug}`}
        image={t.photo}
        schema={personSchema(t)}
      />
      <PageHero
        eyebrow="Therapist"
        title={t.name}
        subtitle={`${t.credentials} — ${t.role}`}
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Our Team', to: '/team' }, { label: t.name }]}
      />
      <section className="section-pad" style={{ background: 'var(--warm-white)' }}>
        <div className="max-w-5xl mx-auto px-6 md:px-10 grid md:grid-cols-[360px_1fr] gap-10">
          <div className="card-soft overflow-hidden self-start reveal">
            <div className="aspect-[4/5]"><img src={t.photo} alt={t.name} className="w-full h-full object-cover" /></div>
            <div className="p-5">
              <div className="font-serif-display text-xl text-[color:var(--deep-sage)]">{t.name}</div>
              <div className="text-xs uppercase tracking-[0.22em] text-[color:var(--sage-dark)] mt-1">{t.credentials}</div>
              <div className="text-sm text-[color:var(--dusty-rose)] mt-1">{t.role}</div>
              <div className="mt-4 space-y-2 text-sm">
                <Link to="/schedule" className="btn-primary w-full justify-center text-sm"><Calendar className="w-4 h-4" /> Schedule with {t.name.split(' ')[0]}</Link>
                <Link to="/contact" className="btn-secondary w-full justify-center text-sm"><Mail className="w-4 h-4" /> Contact Us</Link>
              </div>
            </div>
          </div>

          <div className="reveal">
            {t.quote && (
              <div className="mb-8 flex gap-3">
                <Quote className="w-8 h-8 text-[color:var(--blush-pink)] flex-shrink-0" strokeWidth={1.2} />
                <p className="font-serif-display italic text-xl md:text-2xl text-[color:var(--deep-sage)] leading-snug">{'“'}{t.quote}{'”'}</p>
              </div>
            )}

            <h2 className="font-serif-display text-3xl text-[color:var(--deep-sage)] mb-4">About {t.name.split(' ')[0]}</h2>
            <div className="space-y-4 text-[color:var(--taupe-gray)] leading-relaxed mb-8">
              {bioParas.map((para, i) => <p key={i}>{para}</p>)}
            </div>

            {t.approach && (
              <div className="card-soft p-6 mb-8" style={{ background: 'linear-gradient(135deg, rgba(232,165,169,0.06), rgba(184,190,164,0.1))' }}>
                <div className="eyebrow mb-2">My Approach</div>
                <p className="text-[color:var(--deep-sage)] leading-relaxed">{t.approach}</p>
              </div>
            )}

            {t.vision && (
              <div className="card-soft p-6 mb-8" style={{ background: 'linear-gradient(135deg, rgba(232,165,169,0.08), rgba(255,252,247,0.5))' }}>
                <div className="eyebrow mb-2">My Vision</div>
                <p className="text-[color:var(--deep-sage)] leading-relaxed italic">{t.vision}</p>
              </div>
            )}

            <div className="eyebrow mb-3">Specialties</div>
            <div className="flex flex-wrap gap-2 mb-10">
              {t.specialties.map(s => (
                <span key={s} className="text-xs px-3 py-1.5 rounded-full text-[color:var(--sage-dark)]" style={{ background: 'rgba(184,190,164,0.22)' }}>{s}</span>
              ))}
            </div>

            <Link to="/team" className="inline-flex items-center gap-1.5 text-[color:var(--dusty-rose)]">
              <ArrowRight className="w-4 h-4 rotate-180" /> Back to all therapists
            </Link>
          </div>
        </div>

        {/* Book an appointment + Service Area */}
        <div className="max-w-5xl mx-auto px-6 md:px-10 mt-14">
          <div className="card-soft p-8 md:p-10 text-center reveal" style={{ background: 'linear-gradient(135deg, rgba(232,165,169,0.10), rgba(184,190,164,0.14))' }}>
            <div className="eyebrow mb-3">Ready to begin?</div>
            <h3 className="font-serif-display text-3xl md:text-4xl text-[color:var(--deep-sage)] mb-4">
              Book an appointment with {t.name.split(' ')[0]}
            </h3>
            <p className="text-[color:var(--taupe-gray)] max-w-2xl mx-auto leading-relaxed mb-6">
              Take the first step toward healing — request a session or reach out with any questions. We’ll respond warmly and promptly.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link to="/schedule" className="btn-primary"><Calendar className="w-4 h-4" /> Book an Appointment</Link>
              <Link to="/contact" className="btn-secondary"><Mail className="w-4 h-4" /> Contact Us</Link>
            </div>

            <div className="mt-8 pt-6 border-t max-w-3xl mx-auto flex items-start gap-3 text-left" style={{ borderColor: 'var(--soft-border)' }}>
              <MapPin className="w-5 h-5 text-[color:var(--dusty-rose)] mt-0.5 flex-shrink-0" />
              <p className="text-sm text-[color:var(--taupe-gray)] leading-relaxed">
                Chesapeake Counseling Center is located in Chesapeake, Virginia and serves clients in Chesapeake, Virginia Beach, Norfolk, Suffolk, and surrounding Hampton Roads areas.
              </p>
            </div>
          </div>
        </div>
      </section>
      <FinalCTA />
    </>
  );
}
